import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import AnalyticsView from '@/components/analytics/AnalyticsView';
import { useAuth } from '@/hooks/use-auth';
import {
  akeys, fetchAnalyticsAccess, fetchAnalyticsMe, fetchSources, fetchUnlinkedAds, fmtMoney, PLATFORM_LABEL,
  saveAnalyticsAccess, syncNow, tiktokLink,
} from '@/lib/analytics';
import { fetchClients } from '@/lib/queries';

/** Clinic analytics. Owner and manager only until the owner opens a door below (the server
 *  answers 404 to everyone else, so hiding the page is a courtesy, not the lock). */
export default function AnalyticsPage() {
  const { user } = useAuth();
  const me = useQuery({ queryKey: akeys.me, queryFn: fetchAnalyticsMe });
  const [clientId, setClientId] = useState('');
  useEffect(() => {
    if (!clientId && me.data?.clinics.length) setClientId(me.data.clinics[0].id);
  }, [me.data, clientId]);

  if (me.isLoading) return <p className="text-sm text-muted p-6">Loading...</p>;
  if (!me.data?.can_view) return <Navigate to="/" replace />;
  const admin = me.data.audience === 'admin';

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Analytics" subtitle="What each clinic's pieces did: organic and paid kept apart, money from the CRM" />
      <Card className="p-4 flex flex-wrap items-end gap-4">
        <div className="w-64"><Select label="Clinic" value={clientId} onChange={setClientId}
          options={me.data.clinics.map((c) => ({ value: c.id, label: c.name }))} /></div>
        {admin && <SyncButton />}
      </Card>
      {!me.data.clinics.length && <Card><p className="text-sm text-muted">No clinic has analytics set up yet.</p></Card>}
      {clientId && <AnalyticsView key={clientId} scope="clinic" id={clientId} />}
      {admin && clientId && <SourcesCard clientId={clientId} />}
      {admin && <TikTokConnect defaultClient={clientId} />}
      {admin && clientId && <UnlinkedAds clientId={clientId} />}
      {user?.role === 'owner' && <AccessCard />}
    </div>
  );
}

function SyncButton() {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => syncNow(7),
    onSuccess: () => { toast.success('Sync started: the numbers refresh in a few minutes'); setTimeout(() => qc.invalidateQueries({ queryKey: ['analytics'] }), 90_000); },
    onError: (e: any) => toast.error(e?.response?.status === 409 ? 'A sync is already running' : 'Could not start the sync'),
  });
  return <Button variant="secondary" size="sm" onClick={() => m.mutate()} loading={m.isPending} data-testid="analytics-sync">Sync last 7 days</Button>;
}

const KIND: Record<string, string> = {
  meta_ads: 'Meta ads', tiktok_ads: 'TikTok ads', snap_ads: 'Snapchat ads', instagram: 'Instagram', facebook_page: 'Facebook page',
  tiktok_account: 'TikTok organic', crm: 'CRM',
};
const STATE: Record<string, [string, string]> = {
  ok: ['connected', 'text-emerald-500'], expired: ['reconnect needed', 'text-amber-400'], error: ['error', 'text-red-400'],
  not_connected: ['not connected', 'text-muted'],
};

function SourcesCard({ clientId }: { clientId: string }) {
  const q = useQuery({ queryKey: akeys.sources(clientId), queryFn: () => fetchSources(clientId) });
  return (
    <Card className="p-4" data-testid="analytics-connections">
      <h2 className="text-sm font-semibold text-muted-strong mb-2">Connections</h2>
      {!q.data?.length ? <p className="text-sm text-muted">No source set up for this clinic.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-xs min-w-[560px]">
          <thead><tr className="text-[10px] uppercase tracking-wide text-muted text-left">
            <th className="py-2 pr-2 font-medium">Source</th><th className="py-2 px-2 font-medium">Account</th>
            <th className="py-2 px-2 font-medium">State</th><th className="py-2 px-2 font-medium">Last good sync</th><th className="py-2 px-2 font-medium">Note</th></tr></thead>
          <tbody>{q.data.map((s) => {
            const [label, tone] = STATE[s.status] ?? [s.status, 'text-muted'];
            return (
              <tr key={s.kind + s.external_id} className="border-t border-line text-muted-strong">
                <td className="py-2 pr-2 font-medium">{KIND[s.kind] ?? s.kind}</td>
                <td className="px-2">{s.label ? <span className="text-muted-strong">{s.label}</span> : <span className="font-mono text-[11px]">{s.external_id}</span>}</td>
                <td className={`px-2 ${tone}`}>{label}</td>
                <td className="px-2">{s.last_ok_at ? new Date(s.last_ok_at).toLocaleString() : '-'}</td>
                <td className="px-2 text-muted max-w-[320px] truncate" title={s.last_error ?? ''}>{s.last_error ?? ''}</td>
              </tr>);
          })}</tbody>
        </table></div>
      )}
    </Card>
  );
}

/** Ads with spend whose name carries no piece code: link each on its piece's Analytics card. */
function UnlinkedAds({ clientId }: { clientId: string }) {
  const q = useQuery({ queryKey: akeys.unlinked(clientId), queryFn: () => fetchUnlinkedAds(clientId) });
  const rows = (q.data ?? []).filter((r) => r.spend > 0);
  if (!rows.length) return null;
  return (
    <Card className="p-4" data-testid="analytics-unlinked">
      <h2 className="text-sm font-semibold text-muted-strong">Ads not linked to a piece ({rows.length})</h2>
      <p className="text-xs text-muted mb-2">Their spend counts in the clinic totals but in no piece. Put the piece code (MS-...) in the ad name, or link the ad on the piece's page.</p>
      <div className="overflow-x-auto max-h-80"><table className="w-full text-xs min-w-[560px]">
        <thead><tr className="text-[10px] uppercase tracking-wide text-muted text-left">
          <th className="py-2 pr-2 font-medium">Platform</th><th className="py-2 px-2 font-medium">Ad</th>
          <th className="py-2 px-2 font-medium">Campaign</th><th className="py-2 px-2 font-medium text-right">Spend (all time)</th></tr></thead>
        <tbody>{rows.slice(0, 50).map((r) => (
          <tr key={r.platform + r.ad_id} className="border-t border-line text-muted-strong">
            <td className="py-2 pr-2">{PLATFORM_LABEL[r.platform] ?? r.platform}</td>
            <td className="px-2">{r.ad_name ?? r.ad_id}<div className="font-mono text-[10px] text-muted">{r.ad_id}</div></td>
            <td className="px-2 text-muted">{r.campaign_name ?? '-'}</td>
            <td className="px-2 text-right">{fmtMoney(r.spend)}</td>
          </tr>))}</tbody>
      </table></div>
    </Card>
  );
}

/** The owner's switches. Both start off: nobody but owner and managers sees analytics. */
function AccessCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: akeys.access, queryFn: fetchAnalyticsAccess });
  const m = useMutation({
    mutationFn: saveAnalyticsAccess,
    onSuccess: (d) => { qc.setQueryData(akeys.access, d); toast.success('Access saved'); },
    onError: () => toast.error('Could not save the access'),
  });
  if (!q.data) return null;
  const sw = (k: 'staff' | 'portal', label: string, note: string) => {
    const on = q.data![k];
    return (
      <div className="flex items-start justify-between gap-4 py-3 border-t border-line first:border-t-0">
        <div><div className="text-sm font-medium text-muted-strong">{label}</div><div className="text-xs text-muted">{note}</div></div>
        <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={m.isPending} data-testid={`access-${k}`}
          onClick={() => { if (on || confirm(`Open analytics to ${label.toLowerCase()}?`)) m.mutate({ ...q.data!, [k]: !on }); }}
          className={`relative shrink-0 w-11 h-6 rounded-full transition ${on ? 'bg-accent-600' : 'bg-slate-600/60'}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
    );
  };
  return (
    <Card className="p-4" data-testid="analytics-access">
      <h2 className="text-sm font-semibold text-muted-strong">Who can see analytics</h2>
      <p className="text-xs text-muted mb-1">Owner and managers always can. Only the owner sees these switches.</p>
      {sw('staff', 'Clinic-wide staff', 'Team members who cover a whole clinic (not executors or freelancers), for their clinics only')}
      {sw('portal', 'Clinic portal', "The clinic's main login, for its own clinic, without spend or cost numbers")}
    </Card>
  );
}

/** TikTok gives organic numbers only to an account that approved the KAF app itself. This makes
 *  the approval link (valid 7 days) for any clinic, including one with no analytics yet. */
function TikTokConnect({ defaultClient }: { defaultClient: string }) {
  const clients = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const [clientId, setClientId] = useState('');
  const [link, setLink] = useState<{ url: string; clinic: string } | null>(null);
  const pick = clientId || defaultClient;
  const m = useMutation({
    mutationFn: () => tiktokLink(pick),
    onSuccess: (d) => setLink(d),
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not make the link'),
  });
  return (
    <Card className="p-4" data-testid="analytics-tiktok-connect">
      <h2 className="text-sm font-semibold text-muted-strong">Connect a TikTok account (organic numbers)</h2>
      <p className="text-xs text-muted mb-3">Send the link to whoever logs into the clinic's TikTok account. They tap Approve once;
        the account then appears under Connections. One link per account; it works for 7 days.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64"><Select size="sm" label="Clinic" value={pick} onChange={(v) => { setClientId(v); setLink(null); }}
          options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))} /></div>
        <Button size="sm" variant="secondary" onClick={() => m.mutate()} loading={m.isPending} disabled={!pick}>Make approval link</Button>
      </div>
      {link && (
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={link.url} aria-label={`Approval link for ${link.clinic}`} onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 h-8 rounded-lg border border-line bg-bg px-2 text-xs text-muted-strong" />
          <Button size="sm" onClick={() => { navigator.clipboard?.writeText(link.url).then(() => toast.success('Link copied'), () => toast.error('Copy it by hand')); }}>Copy</Button>
        </div>
      )}
    </Card>
  );
}
